// src/components/ui/ListCard.tsx
//
// Shared list primitive used by FixedExpensesCard / IncomeSourcesCard /
// GoalsCard / LiabilitiesCard / etc. 0.3.1 Preline pass:
//   - rounded-xl (12px) replaces the puffy rounded-3xl
//   - Card title: lucide icon (passed via prop) + regular semibold heading,
//     dropping the 0.3.0 caps-tracking + emoji combo
//   - Summary subtitle: regular text-sm in muted-foreground (no Fraunces
//     italic)
//   - PROGRESS_TONE uses severity-*-dot tokens (replaces the old flat
//     bg-severity-* names which no longer exist after the token rewrite)
//   - `icon` prop accepts ReactNode so callers pass a lucide component or
//     emoji string — both render. Existing callers that pass an emoji
//     string keep working unchanged.

import { motion } from 'framer-motion';
import { type ReactNode, isValidElement } from 'react';
import { cn } from '@/lib/utils';
import { staggerList, staggerItem } from '@/lib/motion';
import { Button } from '@/components/ui/Button';

interface ListCardProps {
  /** lucide icon element or emoji string. */
  icon: ReactNode;
  title: string;
  summary?: string;
  onAdd?: () => void;
  addLabel?: string;
  emptyState?: ReactNode;
  isEmpty?: boolean;
  children?: ReactNode;
}

export function ListCard({
  icon,
  title,
  summary,
  onAdd,
  addLabel = '+ 添加',
  emptyState,
  isEmpty,
  children,
}: ListCardProps) {
  // Emoji strings get a muted-tone treatment; lucide icon elements (passed
  // as React elements) render at their natural ink color so the
  // line-strokes read crisply.
  const iconIsElement = isValidElement(icon);
  return (
    <div className="rounded-xl border bg-card p-4 shadow-card">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                'flex h-5 w-5 items-center justify-center leading-none',
                !iconIsElement && 'text-base opacity-70',
                iconIsElement && 'text-ink-500',
              )}
            >
              {icon}
            </span>
            {/* 0.3.5: text-sm/600 → text-base/600 (14px → 16px) so card
                titles ("固定支出" / "资产" / "目标") visibly outrank their
                row contents and give each card a clear anchor. */}
            <h3 className="truncate text-base font-semibold text-foreground">
              {title}
            </h3>
          </div>
          {summary && (
            <div className="mt-1 text-sm text-ink-500">{summary}</div>
          )}
        </div>
        {onAdd && (
          <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </div>

      {isEmpty && emptyState ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-ink-500">
          {emptyState}
        </div>
      ) : (
        <motion.div
          className="divide-y divide-border"
          variants={staggerList}
          initial="initial"
          animate="animate"
        >
          {children}
        </motion.div>
      )}
    </div>
  );
}

interface ListRowProps {
  /** ReactNode so callers can compose icon + bold label + spans. */
  name: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  rightChip?: ReactNode;
  progress?: { value: number; tone: 'ok' | 'warn' | 'over' };
  onClick?: () => void;
  /** 2px-wide accent bar on the row's left edge — pass a tailwind bg-* class.
   *  Used by callers that want to encode urgency without a Badge. */
  leadingAccent?: string;
}

const PROGRESS_TONE: Record<'ok' | 'warn' | 'over', string> = {
  // 0.3.1 token names: severity-{X}-dot is the saturated dot/bar accent.
  ok: 'bg-primary',
  warn: 'bg-severity-warn-dot',
  over: 'bg-severity-critical-dot',
};

export function ListRow({
  name,
  subtitle,
  trailing,
  rightChip,
  progress,
  onClick,
  leadingAccent,
}: ListRowProps) {
  return (
    <motion.button
      type="button"
      variants={staggerItem}
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        'relative flex min-h-[44px] w-full flex-col justify-center gap-1 py-3 pl-4 pr-2 text-left',
        onClick && 'active:bg-muted/30',
      )}
    >
      {leadingAccent && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-2 left-0 w-[2px] rounded-full',
            leadingAccent,
          )}
        />
      )}
      <div className="flex items-baseline justify-between gap-3">
        {/* 0.3.4: line-clamp-1 → line-clamp-2 so long merchant / item / goal
            names wrap to 2 lines instead of truncating at the row edge on
            mobile (375px). Caller can still pass shorter strings — the
            clamp is just an upper bound. */}
        <span className="line-clamp-2 flex-1 text-sm">{name}</span>
        {trailing && (
          <span className="shrink-0 whitespace-nowrap font-numeric font-bold tabular-nums text-sm">
            {trailing}
          </span>
        )}
      </div>
      {(subtitle || rightChip) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
          {/* 0.3.4: line-clamp-1 → line-clamp-2 for the same reason. Callers
              with 3+ data fields should pass a ReactNode with stacked <div>
              segments instead of a `·`-joined string — that way each field
              gets its own line and nothing gets clipped. */}
          {subtitle && <span className="line-clamp-2">{subtitle}</span>}
          {rightChip}
        </div>
      )}
      {progress && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full', PROGRESS_TONE[progress.tone])}
            style={{ width: `${Math.min(100, Math.max(0, progress.value))}%` }}
          />
        </div>
      )}
    </motion.button>
  );
}
